/* eslint-disable no-underscore-dangle */
import React, { useRef, useCallback, useEffect, memo } from 'react';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ContentBlock from './ContentBlock';

const DEFAULT_ROW_HEIGHT = 264;

const VirtualizedRow = memo(({ index, style, data }) => {
  const {
    blockIds,
    blocks,
    originalBlocks,
    renderContentBlockProps,
    setRowHeight,
    enableCreditUsagePreview,
  } = data;
  const blockId = blockIds[index];
  const rowRef = useRef(null);

  useEffect(() => {
    if (!rowRef.current) return undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) setRowHeight(index, entry.contentRect.height);
    });
    resizeObserver.observe(rowRef.current);
    return () => resizeObserver.disconnect();
  }, [index, setRowHeight]);

  return (
    <div style={style}>
      <div
        ref={rowRef}
        style={{ padding: '24px 24px 16px 24px', boxSizing: 'border-box' }}
        key={blockId}
      >
        {(() => {
          const blockProps = {
            audioType: renderContentBlockProps.audioType,
            blockId,
            block: blocks[blockId],
            originalBlock: originalBlocks[blockId],
            isLastBlock: blockId === blockIds[blockIds.length - 1],
            onOpenDiscardedVoiceCloningDialog:
              renderContentBlockProps.onOpenDiscardedVoiceCloningDialog,
            isFocusing: renderContentBlockProps.currentBlockId === blockId,
            onFocusingBlock: renderContentBlockProps.onFocusingBlock,
            onSaveProject: renderContentBlockProps.onSaveProject,
            voiceDefault: renderContentBlockProps.voiceDefault,
            isDownloading: renderContentBlockProps.isDownloading,
            onOpenOutOfCreditsDialog:
              renderContentBlockProps.onOpenOutOfCreditsDialog,
            checkValidCreateSynthesis:
              renderContentBlockProps.checkValidCreateSynthesis,
            isHighlight: renderContentBlockProps.currentBlockId === blockId,
            handlePlayAudio: renderContentBlockProps.handlePlayAudio,
            onOpenWrongSyntaxDialog:
              renderContentBlockProps.onOpenWrongSyntaxDialog,
            onOpenWrongSyntaxDialogV2:
              renderContentBlockProps.onOpenWrongSyntaxDialogV2,
            projectId: renderContentBlockProps.projectId,
            isPlayingBlock:
              renderContentBlockProps.isPlayingBlock &&
              renderContentBlockProps.currentPlayingBlockId === blockId,
            onConvertBlockToAudio:
              renderContentBlockProps.onConvertBlockToAudio,
            enableCreditUsagePreview,
            onReSynthesizeBlock: renderContentBlockProps.onReSynthesizeBlock,
            blockAudioCurrentTime:
              renderContentBlockProps.blockAudioCurrentTime,
            blockAudioDuration: renderContentBlockProps.blockAudioDuration,
            handleSeekedInBlock: renderContentBlockProps.handleSeekedInBlock,
            currentPlayingBlockId:
              renderContentBlockProps.currentPlayingBlockId,
            onOpenAIGenerateDialog:
              renderContentBlockProps.onOpenAIGenerateDialog,
            foundBlackWords:
              renderContentBlockProps.blockBlackWordsMap?.[blockId] || [],
          };

          return <ContentBlock {...blockProps} />;
        })()}
      </div>
    </div>
  );
});

const VirtualizedContentList = ({
  enableCreditUsagePreview,
  blockIds,
  blocks,
  originalBlocks,
  originalBlockIds = [],
  renderContentBlockProps,
  containerHeight,
  skeletonBlockIds,
}) => {
  const listRef = useRef(null);
  const rowHeights = useRef({});
  const prevBlockIds = useRef(blockIds);

  const setRowHeight = useCallback((index, size) => {
    const newSize = Math.max(size, 80);
    if (rowHeights.current[index] !== newSize) {
      rowHeights.current[index] = newSize;
      listRef.current?.resetAfterIndex(index, true);
    }
  }, []);

  // A row uses the fixed skeleton height only while its block is still a text
  // skeleton (no text yet). Once text arrives the row falls back to its measured
  // height so the rendered text is not clipped.
  const getRowHeight = useCallback(
    (index) => {
      if (skeletonBlockIds?.has(blockIds[index])) return DEFAULT_ROW_HEIGHT;
      return rowHeights.current[index] || DEFAULT_ROW_HEIGHT;
    },
    [skeletonBlockIds, blockIds],
  );

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      // Access the outer element
      // eslint-disable-next-line no-underscore-dangle
      const outerElement = listRef.current._outerRef;

      if (outerElement) {
        outerElement.scrollTop = outerElement.scrollHeight;
      }
    }
  }, [listRef]);

  const findInsertionIndex = (current, previous) => {
    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== previous[i]) return i;
    }
    return -1;
  };

  // Recompute row heights whenever the set of skeleton rows changes (e.g. a
  // block's text arrives during extraction and it switches from the fixed
  // skeleton height to its measured height).
  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [skeletonBlockIds]);

  useEffect(() => {
    const current = blockIds.join(',');
    const previous = prevBlockIds.current.join(',');

    if (current === previous || !listRef.current) return;

    const prevLength = prevBlockIds.current.length;
    const currentLength = blockIds.length;

    const isAddingAtEnd =
      currentLength === prevLength + 1 &&
      prevBlockIds.current.every((id, idx) => id === blockIds[idx]);

    const isRemovingAtEnd =
      currentLength === prevLength - 1 &&
      blockIds.every((id, idx) => id === prevBlockIds.current[idx]);

    const insertionIndex =
      currentLength === prevLength + 1 && !isAddingAtEnd
        ? findInsertionIndex(blockIds, prevBlockIds.current)
        : -1;

    if (isAddingAtEnd) {
      listRef.current.resetAfterIndex(prevLength, false);
      setTimeout(scrollToBottom, 100);
    } else if (insertionIndex !== -1) {
      listRef.current.resetAfterIndex(insertionIndex, false);
    } else if (isRemovingAtEnd) {
      listRef.current.resetAfterIndex(currentLength, false);
    } else {
      listRef.current.resetAfterIndex(0, true);
    }

    prevBlockIds.current = [...blockIds];
  }, [blockIds.length, blockIds.join(','), scrollToBottom]);

  useEffect(() => {
    if (renderContentBlockProps.currentBlockId && listRef.current) {
      const blockIndex = blockIds.indexOf(
        renderContentBlockProps.currentBlockId,
      );
      if (blockIndex !== -1) {
        setTimeout(
          () => listRef.current.resetAfterIndex(blockIndex, true),
          100,
        );
      }
    }
  }, [renderContentBlockProps.currentBlockId, blockIds]);

  // Handle scrolling to specific block (for error notifications, etc.)
  useEffect(() => {
    if (renderContentBlockProps.scrollToBlockId && listRef.current) {
      const blockIndex = blockIds.indexOf(
        renderContentBlockProps.scrollToBlockId,
      );
      if (blockIndex !== -1) {
        setTimeout(() => {
          listRef.current.scrollToItem(blockIndex, 'center');
        }, 100);
      }
    }
  }, [renderContentBlockProps.scrollToBlockId, blockIds]);

  return (
    <div
      style={{
        height: containerHeight || '70vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <List
            ref={listRef}
            height={height}
            width={width}
            className="custom-scrollbar"
            itemCount={blockIds.length}
            itemSize={getRowHeight}
            overscanCount={10}
            itemData={{
              blockIds,
              setRowHeight,
              blocks,
              originalBlocks,
              originalBlockIds,
              renderContentBlockProps,
              enableCreditUsagePreview,
            }}
            itemKey={(index) => blockIds[index]}
            threshold={200}
            style={{ scrollbarWidth: 'thin', overflowX: 'hidden' }}
          >
            {VirtualizedRow}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedContentList;
